import type { ComponentProps } from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@repo/lib/cn';
import { XIcon } from 'lucide-react';

export function Dialog(dialogProps: ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot={'dialog'} {...dialogProps} />;
}

export function DialogTrigger(dialogTriggerProps: ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot={'dialog-trigger'} {...dialogTriggerProps} />;
}

export function DialogPortal(dialogPortalProps: ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot={'dialog-portal'} {...dialogPortalProps} />;
}

export function DialogClose(dialogCloseProps: ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot={'dialog-close'} {...dialogCloseProps} />;
}

export function DialogOverlay(dialogOverlayProps: ComponentProps<typeof DialogPrimitive.Overlay>) {
  const { className, ...props } = dialogOverlayProps;
  return (
    <DialogPrimitive.Overlay
      data-slot={'dialog-overlay'}
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px]',
        className
      )}
      {...props}
    />
  );
}

export function DialogContent(
  dialogContentProps: ComponentProps<typeof DialogPrimitive.Content> & {
    overflowTransparent?: boolean;
    isClosable?: boolean;
  }
) {
  const { className, children, isClosable = true, overflowTransparent = false, ...props } = dialogContentProps;
  return (
    <DialogPortal>
      <DialogOverlay
        className={cn(overflowTransparent && 'bg-transparent')}
        onContextMenu={(e) => {
          e.preventDefault();
        }}
      />
      <DialogPrimitive.Content
        className={cn(
          'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed z-50 flex flex-col gap-0 overflow-hidden border p-0 duration-150',
          'max-h-[calc(100%-2rem)] w-[calc(100%-2rem)] max-w-[calc(100%-2rem)]',
          'rounded-lg shadow-lg sm:max-w-5xl',
          'top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]',
          className
        )}
        {...props}
      >
        {isClosable && (
          <DialogPrimitive.Close
            className={
              'text-muted-foreground outline-focus-ring absolute top-1 right-1 grid h-9 w-9 cursor-pointer place-items-center rounded-sm opacity-75 outline-0 transition-opacity hover:opacity-100 focus:outline-none focus-visible:outline-3 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-75'
            }
          >
            <XIcon className={'pointer-events-none size-4 shrink-0'} />
            <span className={'sr-only'}>Close</span>
          </DialogPrimitive.Close>
        )}
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

export function DialogHeader(dialogHeaderProps: ComponentProps<'div'>) {
  const { className, ...props } = dialogHeaderProps;
  return <div data-slot={'dialog-header'} className={cn('flex flex-row gap-3 text-left p-4', className)} {...props} />;
}

export function DialogBody(dialogBodyProps: ComponentProps<'div'>) {
  const { className, ...props } = dialogBodyProps;
  return <div data-slot={'dialog-body'} className={cn('relative contents space-y-0 text-left', className)} {...props} />;
}

export function DialogFooter(dialogFooterProps: ComponentProps<'div'>) {
  const { className, ...props } = dialogFooterProps;
  return <div data-slot={'dialog-footer'} className={cn('flex justify-end space-x-3 p-4', className)} {...props} />;
}

export function DialogTitle(dialogTitleProps: ComponentProps<typeof DialogPrimitive.Title>) {
  const { className, ...props } = dialogTitleProps;
  return <DialogPrimitive.Title data-slot={'dialog-title'} className={cn('max-w-full truncate pr-8 text-lg font-semibold tracking-tight whitespace-nowrap', className)} {...props} />;
}

export function DialogDescription(dialogDescriptionProps: ComponentProps<typeof DialogPrimitive.Description>) {
  const { className, ...props } = dialogDescriptionProps;
  return <DialogPrimitive.Description data-slot={'dialog-description'} className={cn('text-muted-foreground text-sm', className)} {...props} />;
}
